/**
  * @class mmodEnroll
  * @description
  * Enrolment module is used on first application start to enrol the new application
  * to Mosy mServer (see chapter Enrolment).
  * Interface is practically the same on mPatrol and mOffice.
  * Enrolment module allows user (most often technical person) to enter enrolment
  * parameters - most importantly URL of mServer where to enrol.
  * Module is implemented without workflow, as a pure module named mmodEnroll.
  * Module reacts to these messages:
  * - mmAppEnrollNeeded - received from msysDomainClient to indicate that enrolment screen needs to be displayed
  * - mmAppEnrolling - received from msysDomainClient that application is currently disabled and approval is pending
  * - mmAppIdentified - received from msysDomainClient to indicate that application has been
  *   successfully identified - which means enrolment screen can be closed
  * Enrolment module can be configured to perform automatic enrolment without showing the user screen.
  */

import mmodModule from 'mmodModule'
import { Device } from '@capacitor/device'
const mfs = require('msysFS')

export default class mmodEnroll extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: {
        type: 'object',
        default: {
          type: 'float',
          x: 0,
          y: 0,
          width: '100%',
          height: '100%',
          order: 1,
        },
        description: 'Initial window setup; same structure as required by `mmWindowSet`',
      },
      auto_enroll: {
        description: 'if true, performs automatic enrollment request to mServer without user interaction',
        type: 'boolean',
        default: false,
      },
      server: {
        type: 'string',
        description: 'default enrollment server',
      },
      server_capacitor: {
        type: 'string',
        description: 'default enrollment server for Capacitor platform',
      },
      server_nwjs: {
        type: 'string',
        description: 'default enrollment server for NW.js platform',
      },
      server_node: {
        type: 'string',
        description: 'default enrollment server for node platform',
      },
      server_web: {
        type: 'string',
        description: 'default enrollment server for web platform',
      },
      server_unknown: {
        type: 'string',
        description: 'default enrollment server for unknown platform',
      },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * initialization of GUI components
  */
  initWindow() {
    this.mmStart()
  } // initWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * creates GUI components
  */
  async createGui() {
    if(this.gui_ready) return
    this.loadForm(require('./mmodEnroll.json'))
    const configured_server = this.config['server_'+mfs.platform()] ?? this.config.server
    const automatic_server =
      /^https:$/.test(window.location.protocol) ? 'wss://'+window.location.host.replace(/^localhost:7/, 'localhost:8') :
        /^http:$/.test(window.location.protocol) ? 'ws://'+window.location.host.replace(/^localhost:7/, 'localhost:8') :
          'ws://localhost:8000'

    this.el.find('@server').value = configured_server || automatic_server

    // If we are in cordova Platform we try to detect Serial number
    if(mfs.platform() === 'capacitor' && Device != null) {
      const device = await Device.getInfo()
      if(device.serial) {
        this.el.find('@note').value = MOSY_APP + ' SN:' + device.serial
      } else {
        this.el.find('@note').value = MOSY_APP
      }
    } else {
      this.el.find('@note').value = MOSY_APP
    }

    if(/^https?:$/.test(window.location.protocol)) {
      mfs.readFile(window.location.protocol+'//'+window.location.host.replace(/^localhost:7/, 'localhost:8')+'/myip', (err, data)=>{
        data = err ? '' : (''+data)
        if(data.length>0 && !/</.test(data)) {
          this.el.find('@note').value = MOSY_APP+' '+data
        }
      })
    }
    this.gui_ready = true
  } // createGui

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that it successfully identifies with given app_id
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {string} [params.app_key] - private key of application
  * @param {string} [params.app_cert] - application certificate signed by deployment PKI CA
  * @param {string} [params.unit_id] - unique identifier for the logical patrolling unit
  */
  mmAppIdentified(sender, params) {
    super.mmAppIdentified(sender, params)
    this.el.hidden = !!params.app_id
    if(!params.app_id) {
      this.createGui()
      this.el.views = 'connect'
    }
  } // mmAppIdentified

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners (primarily mmodEnroll) that enrollment request is required
  * @param {object} sender - message sender
  * @param {object} params - not used
  */
  mmAppEnrollNeeded(sender, params) {
    this.createGui()
    this.el.hidden = false
    if(this.config.auto_enroll) {
      this.el.hidden = false
      this.el.views = 'enrolling'
      setTimeout(()=>{
        this.publish('msysDomainClient', 'mmAppEnroll', {
          server: this.el.find('@server').value,
          note: this.el.find('@note').value,
        })
      }, 1000)
    }else{
      this.el.views = 'enroll'
    }
  } // mmAppEnrollNeeded

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that application awaits enrollment approval on server
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.app_id - unique application ID
  */
  mmAppEnrolling(sender, params) {
    this.createGui()
    this.el.hidden = false
    this.el.views = 'enrollingwait'
  } // mmAppEnrolling

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sent by &lt;mele-container> or derived class to parent module when form is submitted
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.holder - key attribute of the form
  * @param {string} [params.button] - key attribute of the button
  * @param {string} [params.datakey] - key attribute of the content
  * @param {object} params.values - hash of key:value of contained editable inputs
  * @param {boolean} [params.invalid] - true if any of form's inputs is invalid
  * @param {boolean} [params.conflict] - true if any of form's inputs has conflict
  */
  mmFormSubmit(sender, params) {
    this.el.views = 'enrolling'
    this.publish('msysDomainClient', 'mmAppEnroll', params.values)
  } // mmFormSubmit

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sent by &lt;mele-container> or derived class to parent module when form is cancelled
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.holder - key attribute of the form
  * @param {string} [params.datakey] - key attribute of the content
  */
  mmFormCancel(sender, params) {
    this.el.views = 'enroll'
    this.publish('msysDomainClient', 'mmAppEnrollAbort')
  } // mmFormCancel

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by &lt;mele-button> to parent module when a button is clicked
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of button's parent container
  * @param {string} params.button - key attribute of the button
  * @param {integer} [params.count] - number of times button was clicked
  * @param {boolean} [params.long] - shows if press was long or not
  */
  mmButtonClicked(sender, params) {
    if(params.button==='reenroll') {
      this.publish('msysDomainClient', 'mmAppEnroll')
    }
  } // mmButtonClicked
} // mmodEnroll
